import React, { useEffect, useMemo, useState } from "react";
import { api, BACKEND_URL, formatCurrency, formatDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, TrendUp, TrendDown, Trash, FilePdf, FileCsv, ChartLineUp } from "@phosphor-icons/react";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell } from "recharts";
import { toast } from "sonner";

const MONTHS_TR_SHORT = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];
const CAT_LABELS = { general: "Genel", travel: "Yol", material: "Materyal", marketing: "Reklam", other: "Diğer" };
const CAT_COLORS = { general: "#3A5A40", travel: "#D97757", material: "#7C5E99", marketing: "#E8A33D", other: "#5C6A5D" };

function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function startOfMonth(d = new Date()) { return ymd(new Date(d.getFullYear(), d.getMonth(), 1)); }
function endOfMonth(d = new Date()) { return ymd(new Date(d.getFullYear(), d.getMonth() + 1, 0)); }
function startOfYear(d = new Date()) { return ymd(new Date(d.getFullYear(), 0, 1)); }
function endOfYear(d = new Date()) { return ymd(new Date(d.getFullYear(), 11, 31)); }
function addMonths(date, n) { const d = new Date(date); d.setMonth(d.getMonth() + n); return d; }
function monthLabel(ym) {
  if (!ym || !ym.includes("-")) return ym || "";
  const [y, m] = ym.split("-");
  return `${MONTHS_TR_SHORT[Number(m) - 1]} ${y}`;
}
function downloadFromUrl(path, params, fallbackName) {
  const qs = new URLSearchParams(params).toString();
  const url = `${BACKEND_URL}/api${path}?${qs}`;
  return fetch(url, { credentials: "include" }).then(async (r) => {
    if (!r.ok) {
      const txt = await r.text().catch(() => "");
      throw new Error(txt || `İndirme başarısız (HTTP ${r.status})`);
    }
    const blob = await r.blob();
    const cd = r.headers.get("content-disposition") || "";
    const m = cd.match(/filename\*=UTF-8''([^;]+)/i) || cd.match(/filename="?([^";]+)"?/i);
    let name = fallbackName;
    if (m && m[1]) {
      try { name = decodeURIComponent(m[1]); } catch { name = m[1]; }
    }
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(a.href);
  });
}

export default function AccountingPage() {
  const { user } = useAuth();
  const [payments, setPayments] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [students, setStudents] = useState([]);
  const [monthly, setMonthly] = useState([]);
  const [expOpen, setExpOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [expForm, setExpForm] = useState({ amount: "", category: "general", date: new Date().toISOString().slice(0, 10), note: "" });
  const [payForm, setPayForm] = useState({ student_id: "", amount: "", method: "cash", date: new Date().toISOString().slice(0, 10), note: "" });

  async function load() {
    const [p, e, s, m] = await Promise.all([
      api.get("/payments"),
      api.get("/expenses"),
      api.get("/students"),
      api.get("/stats/monthly", { params: { months: 12 } }),
    ]);
    setPayments(p.data); setExpenses(e.data); setStudents(s.data); setMonthly(m.data);
  }
  useEffect(() => { load(); }, []);

  async function addExpense() {
    try {
      await api.post("/expenses", {
        amount: Number(expForm.amount),
        category: expForm.category,
        date: new Date(expForm.date).toISOString(),
        note: expForm.note,
      });
      toast.success("Gider eklendi.");
      setExpOpen(false);
      setExpForm({ amount: "", category: "general", date: new Date().toISOString().slice(0, 10), note: "" });
      load();
    } catch { toast.error("Hata."); }
  }

  async function addPayment() {
    try {
      if (!payForm.student_id) return toast.error("Öğrenci seçin.");
      await api.post("/payments", {
        student_id: payForm.student_id,
        amount: Number(payForm.amount),
        method: payForm.method,
        date: new Date(payForm.date).toISOString(),
        note: payForm.note,
      });
      toast.success("Ödeme eklendi.");
      setPayOpen(false);
      setPayForm({ student_id: "", amount: "", method: "cash", date: new Date().toISOString().slice(0, 10), note: "" });
      load();
    } catch { toast.error("Hata."); }
  }

  async function delPayment(id) {
    try { await api.delete(`/payments/${id}`); toast.success("Silindi."); load(); } catch { toast.error("Silinemedi."); }
  }
  async function delExpense(id) {
    try { await api.delete(`/expenses/${id}`); toast.success("Silindi."); load(); } catch { toast.error("Silinemedi."); }
  }

  const c = user?.currency || "TRY";
  const totalPay = payments.reduce((s, p) => s + p.amount, 0);
  const totalExp = expenses.reduce((s, p) => s + p.amount, 0);
  const nameById = (id) => students.find((s) => s.id === id)?.name || "—";

  return (
    <div className="space-y-6" data-testid="accounting-page">
      <div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Muhasebe</h1>
        <p className="text-muted-foreground text-sm mt-1">Gelir, gider ve raporlarınız tek yerde.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <StatCard label="Toplam alınan ödeme" value={formatCurrency(totalPay, c)} Icon={TrendUp} tone="success" />
        <StatCard label="Toplam gider" value={formatCurrency(totalExp, c)} Icon={TrendDown} tone="accent" />
        <StatCard label="Net" value={formatCurrency(totalPay - totalExp, c)} Icon={TrendUp} tone="primary" />
      </div>

      <div className="bg-white border border-border rounded-lg p-5">
        <h2 className="font-display text-lg font-bold mb-3">12 Aylık Eğilim</h2>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={monthly}>
              <CartesianGrid stroke="#E5E4E0" vertical={false} />
              <XAxis dataKey="month" stroke="#5C6A5D" fontSize={12} />
              <YAxis stroke="#5C6A5D" fontSize={12} />
              <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E5E4E0", borderRadius: 8 }} formatter={(v) => formatCurrency(v, c)} />
              <Legend />
              <Line type="monotone" dataKey="income" stroke="#3A5A40" strokeWidth={2.5} dot={{ r: 4 }} name="Gelir" />
              <Line type="monotone" dataKey="expenses" stroke="#D97757" strokeWidth={2.5} dot={{ r: 4 }} name="Gider" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <IncomeExpenseReport currency={c} />

      <Tabs defaultValue="income">
        <TabsList>
          <TabsTrigger value="income" data-testid="tab-income">Gelirler</TabsTrigger>
          <TabsTrigger value="expense" data-testid="tab-expense">Giderler</TabsTrigger>
        </TabsList>
        <TabsContent value="income" className="mt-4">
          <div className="flex justify-end mb-3">
            <Button onClick={() => setPayOpen(true)} className="bg-[#3A5A40] hover:bg-[#2E4833] text-white" data-testid="accounting-add-payment-button">
              <Plus size={14} className="mr-1" /> Ödeme Ekle
            </Button>
          </div>
          <div className="bg-white border border-border rounded-lg overflow-hidden">
            {payments.length === 0 ? <Empty label="Henüz ödeme yok" /> : (
              <>
                {/* Desktop table */}
                <table className="w-full text-sm hidden md:table">
                  <thead className="bg-muted/40 text-xs text-muted-foreground">
                    <tr>
                      <th className="text-left px-4 py-2">Tarih</th>
                      <th className="text-left px-4 py-2">Öğrenci</th>
                      <th className="text-left px-4 py-2">Yöntem</th>
                      <th className="text-left px-4 py-2">Not</th>
                      <th className="text-right px-4 py-2">Tutar</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {payments.map((p) => (
                      <tr key={p.id}>
                        <td className="px-4 py-2">{formatDate(p.date)}</td>
                        <td className="px-4 py-2">{nameById(p.student_id)}</td>
                        <td className="px-4 py-2">{p.method}</td>
                        <td className="px-4 py-2 text-muted-foreground">{p.note || "—"}</td>
                        <td className="px-4 py-2 text-right font-mono text-[#2E7D32] font-semibold">{formatCurrency(p.amount, c)}</td>
                        <td className="px-4 py-2 text-right">
                          <Button size="icon" variant="ghost" onClick={() => delPayment(p.id)} className="h-7 w-7"><Trash size={14} /></Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {/* Mobile card list */}
                <ul className="md:hidden divide-y divide-border" data-testid="payments-card-list">
                  {payments.map((p) => (
                    <li key={p.id} className="p-3 flex items-start gap-3" data-testid={`payment-card-${p.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-medium truncate">{nameById(p.student_id)}</div>
                          <div className="font-mono text-sm text-[#2E7D32] font-semibold whitespace-nowrap">{formatCurrency(p.amount, c)}</div>
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5 truncate">
                          {formatDate(p.date)} · {p.method}{p.note ? ` · ${p.note}` : ""}
                        </div>
                      </div>
                      <Button size="icon" variant="ghost" onClick={() => delPayment(p.id)} className="h-8 w-8 text-muted-foreground -mr-1"><Trash size={14} /></Button>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </TabsContent>
        <TabsContent value="expense" className="mt-4">
          <div className="flex justify-end mb-3">
            <Button onClick={() => setExpOpen(true)} className="bg-[#D97757] hover:bg-[#BF6446] text-white" data-testid="accounting-add-expense-button">
              <Plus size={14} className="mr-1" /> Gider Ekle
            </Button>
          </div>
          <div className="bg-white border border-border rounded-lg overflow-hidden">
            {expenses.length === 0 ? <Empty label="Henüz gider yok" /> : (
              <>
                {/* Desktop table */}
                <table className="w-full text-sm hidden md:table">
                  <thead className="bg-muted/40 text-xs text-muted-foreground">
                    <tr>
                      <th className="text-left px-4 py-2">Tarih</th>
                      <th className="text-left px-4 py-2">Kategori</th>
                      <th className="text-left px-4 py-2">Not</th>
                      <th className="text-right px-4 py-2">Tutar</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {expenses.map((e) => (
                      <tr key={e.id}>
                        <td className="px-4 py-2">{formatDate(e.date)}</td>
                        <td className="px-4 py-2">{CAT_LABELS[e.category] || e.category}</td>
                        <td className="px-4 py-2 text-muted-foreground">{e.note || "—"}</td>
                        <td className="px-4 py-2 text-right font-mono text-[#D97757] font-semibold">-{formatCurrency(e.amount, c)}</td>
                        <td className="px-4 py-2 text-right">
                          <Button size="icon" variant="ghost" onClick={() => delExpense(e.id)} className="h-7 w-7"><Trash size={14} /></Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {/* Mobile card list */}
                <ul className="md:hidden divide-y divide-border" data-testid="expenses-card-list">
                  {expenses.map((e) => (
                    <li key={e.id} className="p-3 flex items-start gap-3" data-testid={`expense-card-${e.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <div className="font-medium truncate">{CAT_LABELS[e.category] || e.category}</div>
                          <div className="font-mono text-sm text-[#D97757] font-semibold whitespace-nowrap">-{formatCurrency(e.amount, c)}</div>
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5 truncate">
                          {formatDate(e.date)}{e.note ? ` · ${e.note}` : ""}
                        </div>
                      </div>
                      <Button size="icon" variant="ghost" onClick={() => delExpense(e.id)} className="h-8 w-8 text-muted-foreground -mr-1"><Trash size={14} /></Button>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Add Payment Dialog */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Ödeme ekle</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Öğrenci</Label>
              <Select value={payForm.student_id} onValueChange={(v) => setPayForm({ ...payForm, student_id: v })}>
                <SelectTrigger data-testid="payment-student-select"><SelectValue placeholder="Öğrenci seçin" /></SelectTrigger>
                <SelectContent>
                  {students.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Tutar ({c})</Label>
              <Input type="number" step="0.01" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Tarih</Label>
              <Input type="date" value={payForm.date} onChange={(e) => setPayForm({ ...payForm, date: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Yöntem</Label>
              <Select value={payForm.method} onValueChange={(v) => setPayForm({ ...payForm, method: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Nakit</SelectItem>
                  <SelectItem value="bank">Banka</SelectItem>
                  <SelectItem value="card">Kart</SelectItem>
                  <SelectItem value="other">Diğer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Not</Label>
              <Input value={payForm.note} onChange={(e) => setPayForm({ ...payForm, note: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(false)}>Vazgeç</Button>
            <Button onClick={addPayment} className="bg-[#3A5A40] hover:bg-[#2E4833] text-white">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Expense Dialog */}
      <Dialog open={expOpen} onOpenChange={setExpOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">Gider ekle</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Tutar ({c})</Label>
              <Input type="number" step="0.01" value={expForm.amount} onChange={(e) => setExpForm({ ...expForm, amount: e.target.value })} data-testid="expense-form-amount" />
            </div>
            <div className="space-y-1.5">
              <Label>Tarih</Label>
              <Input type="date" value={expForm.date} onChange={(e) => setExpForm({ ...expForm, date: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Kategori</Label>
              <Select value={expForm.category} onValueChange={(v) => setExpForm({ ...expForm, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">Genel</SelectItem>
                  <SelectItem value="travel">Yol</SelectItem>
                  <SelectItem value="material">Materyal</SelectItem>
                  <SelectItem value="marketing">Reklam</SelectItem>
                  <SelectItem value="other">Diğer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Not</Label>
              <Input value={expForm.note} onChange={(e) => setExpForm({ ...expForm, note: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExpOpen(false)}>Vazgeç</Button>
            <Button onClick={addExpense} className="bg-[#D97757] hover:bg-[#BF6446] text-white" data-testid="expense-form-save">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ label, value, Icon, tone }) {
  const tones = { success: "bg-[#E8F5E9] text-[#2E7D32]", accent: "bg-[#FFF3E0] text-[#E65100]", primary: "bg-[#3A5A40] text-white" };
  return (
    <div className="bg-white border border-border rounded-lg p-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
          <div className="font-display text-2xl font-bold mt-2">{value}</div>
        </div>
        <div className={`w-10 h-10 rounded-md flex items-center justify-center ${tones[tone]}`}><Icon size={20} weight="duotone" /></div>
      </div>
    </div>
  );
}
function Empty({ label }) {
  return <div className="py-12 text-center text-sm text-muted-foreground">{label}</div>;
}

function IncomeExpenseReport({ currency }) {
  const [start, setStart] = useState(startOfMonth());
  const [end, setEnd] = useState(endOfMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (start > end) return;
    setLoading(true);
    api.get("/reports/income-expense", { params: { start, end } })
      .then((r) => setData(r.data))
      .catch(() => toast.error("Gelir-Gider raporu yüklenemedi."))
      .finally(() => setLoading(false));
  }, [start, end]);

  function applyPreset(key) {
    const t = new Date();
    if (key === "this-month") { setStart(startOfMonth(t)); setEnd(endOfMonth(t)); }
    else if (key === "last-month") { const d = addMonths(t, -1); setStart(startOfMonth(d)); setEnd(endOfMonth(d)); }
    else if (key === "last-3") { setStart(ymd(addMonths(t, -2))); setEnd(endOfMonth(t)); }
    else if (key === "last-6") { setStart(ymd(addMonths(t, -5))); setEnd(endOfMonth(t)); }
    else if (key === "this-year") { setStart(startOfYear(t)); setEnd(endOfYear(t)); }
    else if (key === "last-year") { const d = new Date(t.getFullYear() - 1, 0, 1); setStart(startOfYear(d)); setEnd(endOfYear(d)); }
  }

  async function downloadPdf() {
    if (start > end) return toast.error("Tarih aralığı geçersiz.");
    setBusy(true);
    try {
      await downloadFromUrl("/reports/income-expense/pdf", { start, end }, `gelir_gider_${start}_${end}.pdf`);
      toast.success("PDF indirildi.");
    } catch (e) { toast.error(e.message || "PDF oluşturulamadı."); } finally { setBusy(false); }
  }
  async function downloadCsv() {
    if (start > end) return toast.error("Tarih aralığı geçersiz.");
    setBusy(true);
    try {
      await downloadFromUrl("/reports/income-expense/csv", { start, end }, `gelir_gider_${start}_${end}.csv`);
      toast.success("CSV indirildi.");
    } catch (e) { toast.error(e.message || "CSV oluşturulamadı."); } finally { setBusy(false); }
  }

  const t = data?.totals || { earned: 0, paid: 0, expense: 0, net: 0 };
  const monthly = useMemo(() => (data?.monthly || []).map((m) => ({ ...m, label: monthLabel(m.month) })), [data]);
  const byCategory = useMemo(() => (data?.by_category || []).map((c) => ({ ...c, label: CAT_LABELS[c.category] || c.category, color: CAT_COLORS[c.category] || "#5C6A5D" })), [data]);

  return (
    <div className="bg-white border border-border rounded-lg p-5 space-y-4" data-testid="income-expense-report">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-lg font-bold flex items-center gap-2">
            <ChartLineUp size={20} weight="duotone" className="text-[#3A5A40]" />
            Gelir-Gider Raporu
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">Belirlediğin tarih aralığındaki toplu finans özeti.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={downloadCsv} disabled={busy} variant="outline" size="sm" data-testid="ie-csv-button">
            <FileCsv size={14} className="mr-1" weight="duotone" /> CSV
          </Button>
          <Button onClick={downloadPdf} disabled={busy} size="sm" className="bg-[#D97757] hover:bg-[#BF6446] text-white" data-testid="ie-pdf-button">
            <FilePdf size={14} className="mr-1" weight="duotone" /> {busy ? "Hazırlanıyor…" : "PDF"}
          </Button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label>Başlangıç</Label>
          <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="ie-start-date" />
        </div>
        <div className="space-y-1.5">
          <Label>Bitiş</Label>
          <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="ie-end-date" />
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <span className="text-xs text-muted-foreground self-center mr-1">Hızlı:</span>
        {[
          ["this-month", "Bu ay"],
          ["last-month", "Geçen ay"],
          ["last-3", "Son 3 ay"],
          ["last-6", "Son 6 ay"],
          ["this-year", "Bu yıl"],
          ["last-year", "Geçen yıl"],
        ].map(([k, label]) => (
          <button
            key={k}
            onClick={() => applyPreset(k)}
            data-testid={`ie-preset-${k}`}
            className="text-xs px-3 py-1 rounded-full border border-border bg-white hover:bg-[#FAF9F6] hover:border-[#3A5A40] hover:text-[#3A5A40] transition-colors"
          >{label}</button>
        ))}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="ie-stats">
        <MiniStat label="Hak edilen" value={formatCurrency(t.earned, currency)} tone="primary" />
        <MiniStat label="Tahsil edilen" value={formatCurrency(t.paid, currency)} tone="success" />
        <MiniStat label="Toplam gider" value={formatCurrency(t.expense, currency)} tone="accent" />
        <MiniStat label="Net kâr" value={formatCurrency(t.net, currency)} tone={t.net >= 0 ? "success" : "accent"} />
      </div>

      {loading ? (
        <div className="py-8 text-center text-sm text-muted-foreground">Yükleniyor…</div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Aylık trend</div>
            {monthly.length === 0 ? (
              <div className="h-56 flex items-center justify-center text-sm text-muted-foreground border border-dashed border-border rounded-md">Veri yok</div>
            ) : (
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={monthly}>
                    <CartesianGrid stroke="#E5E4E0" vertical={false} />
                    <XAxis dataKey="label" stroke="#5C6A5D" fontSize={11} />
                    <YAxis stroke="#5C6A5D" fontSize={11} />
                    <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E5E4E0", borderRadius: 8 }} formatter={(v) => formatCurrency(v, currency)} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="earned" stroke="#3A5A40" strokeWidth={2} dot={{ r: 3 }} name="Hak edilen" />
                    <Line type="monotone" dataKey="expense" stroke="#D97757" strokeWidth={2} dot={{ r: 3 }} name="Gider" />
                    <Line type="monotone" dataKey="net" stroke="#2E7D32" strokeWidth={2} strokeDasharray="4 4" dot={false} name="Net" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Gider kategorileri</div>
            {byCategory.length === 0 ? (
              <div className="h-56 flex items-center justify-center text-sm text-muted-foreground border border-dashed border-border rounded-md">Gider yok</div>
            ) : (
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={byCategory} layout="vertical" margin={{ left: 8, right: 8 }}>
                    <CartesianGrid stroke="#E5E4E0" horizontal={false} />
                    <XAxis type="number" stroke="#5C6A5D" fontSize={11} />
                    <YAxis dataKey="label" type="category" stroke="#5C6A5D" fontSize={11} width={80} />
                    <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E5E4E0", borderRadius: 8 }} formatter={(v) => formatCurrency(v, currency)} />
                    <Bar dataKey="amount" name="Gider" radius={[0, 4, 4, 0]}>
                      {byCategory.map((c, i) => <Cell key={i} fill={c.color} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value, tone = "neutral" }) {
  const cls = {
    primary: "text-[#3A5A40]",
    success: "text-[#2E7D32]",
    accent: "text-[#D97757]",
    neutral: "text-foreground",
  }[tone];
  return (
    <div className="border border-border rounded-md p-3 bg-[#FAF9F6]">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`font-display text-base md:text-lg font-bold mt-1 ${cls}`}>{value}</div>
    </div>
  );
}
